import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import type { MessageDescriptor } from '@lingui/core';
import { Search } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@shared/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@shared/ui/dialog';
import { Input } from '@shared/ui/input';
import { localizePath, useLocale } from '@shared/i18n';
import { getNavItemsForRole, type NavigationRegistryItem } from '@shared/lib/navigation';
import type { UserRole } from '@shared/types/auth';

export interface PaletteEntry {
  key: string;
  label: string;
  path: string;
  Icon: NavigationRegistryItem['icon'];
}

export const filterPaletteEntries = (entries: PaletteEntry[], query: string): PaletteEntry[] => {
  const needle = query.trim().toLowerCase();
  return needle ? entries.filter(entry => entry.label.toLowerCase().includes(needle)) : entries;
};

export const CommandPalette: React.FC<{ role: UserRole }> = ({ role }) => {
  const { i18n } = useLingui();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [active, setActive] = React.useState(0);

  const entries = React.useMemo<PaletteEntry[]>(
    () =>
      getNavItemsForRole(role).map(item => ({
        key: item.key,
        label: i18n._(item.label as MessageDescriptor),
        path: item.path,
        Icon: item.icon,
      })),
    [role, i18n]
  );
  const results = filterPaletteEntries(entries, query);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setQuery('');
      setActive(0);
    }
  };

  const go = (entry: PaletteEntry) => {
    onOpenChange(false);
    navigate(localizePath(entry.path, locale));
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (results.length === 0) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive(prev => (prev + step + results.length) % results.length);
    } else if (event.key === 'Enter' && results[active]) {
      event.preventDefault();
      go(results[active]);
    }
  };

  return (
    <>
      <Button
        type='button'
        variant='outline'
        size='sm'
        className='hidden h-9 w-[200px] justify-start gap-2 text-muted-foreground lg:flex xl:w-[260px]'
        aria-label={t({
          id: 'shared.layout.CommandPalette.open-label',
          message: 'Search pages (Command or Control plus K)',
        })}
        onClick={() => setOpen(true)}
      >
        <Search className='size-4' aria-hidden />
        <span className='flex-1 text-left'>
          <Trans id='shared.layout.CommandPalette.open'>Search…</Trans>
        </span>
        <kbd className='rounded border px-1.5 text-xs font-medium'><Trans id='shared.layout.CommandPalette.k'>⌘K</Trans></kbd>
      </Button>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className='gap-3 p-4 sm:max-w-md' showCloseButton={false}>
          <DialogTitle className='sr-only'>
            <Trans id='shared.layout.CommandPalette.title'>Go to page</Trans>
          </DialogTitle>
          <DialogDescription className='sr-only'>
            <Trans id='shared.layout.CommandPalette.description'>
              Type to filter, use the arrow keys to choose, and press Enter to open.
            </Trans>
          </DialogDescription>
          <Input
            autoFocus
            role='combobox'
            aria-expanded
            aria-controls='command-palette-list'
            aria-activedescendant={
              results[active] ? `command-palette-${results[active].key}` : undefined
            }
            aria-label={t({ id: 'shared.layout.CommandPalette.input', message: 'Search pages' })}
            placeholder={t({
              id: 'shared.layout.CommandPalette.placeholder',
              message: 'Go to…',
            })}
            value={query}
            onChange={event => {
              setQuery(event.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
          />
          <ul id='command-palette-list' role='listbox' className='max-h-72 overflow-y-auto'>
            {results.map((entry, index) => (
              <li
                key={entry.key}
                id={`command-palette-${entry.key}`}
                role='option'
                aria-selected={index === active}
                className={`flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm ${
                  index === active ? 'bg-accent text-accent-foreground' : ''
                }`}
                onMouseEnter={() => setActive(index)}
                onClick={() => go(entry)}
              >
                <entry.Icon className='size-4' />
                {entry.label}
              </li>
            ))}
          </ul>
          {results.length === 0 ? (
            <p className='px-2 py-3 text-sm text-muted-foreground'>
              <Trans id='shared.layout.CommandPalette.empty'>No matching pages</Trans>
            </p>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};
